import { RefObject } from 'react';
import useResponsive from '@creditflowresearch/uilib/hooks/useResponsive';

import useDelayedHover from '@creditflowresearch/uilib/PanelControl/useDelayedHover';
import useTriggerToggle from '@creditflowresearch/uilib/PanelControl/usePanelToggle';
import { PANEL_TIMINGS } from '@creditflowresearch/uilib/PanelControl/timings';
import type { IPanelInteraction } from '@creditflowresearch/uilib/PanelControl/types';

interface IHoverInteractionOptions {
  panelId: string,
  positioning: 'relative' | 'fixed',
  offsetSelector?: string,
  /** Delay before opening on hover (ms). Defaults to `PANEL_TIMINGS.openDelay` */
  delayDuration?: number,
  /** Skip delay window for fast switching between items (ms). Defaults to `PANEL_TIMINGS.skipDelay` */
  skipDelayDuration?: number,
  /** Delay before closing on leave (ms). Defaults to `PANEL_TIMINGS.leaveDelay` */
  leaveDelay?: number,
}

const updatePanelPosition = (container: HTMLElement): void => {
  const rect = container.getBoundingClientRect();
  container.style.setProperty('--activator-top', `${ rect.top }px`);
  container.style.setProperty('--activator-height', `${ rect.height }px`);
};

export const createHoverInteraction = (options: IHoverInteractionOptions) =>
  (containerRef: RefObject<HTMLDivElement | null>): IPanelInteraction => {
    const isMobile = useResponsive(({ width }) => width < 1024);

    const { isOpen, togglePanel, panelRef } = useTriggerToggle({
      panelId: options.panelId,
      positioning: options.positioning,
      offsetSelector: options.offsetSelector,
    });

    const isHovered = useDelayedHover(containerRef, {
      delayDuration: options.delayDuration ?? PANEL_TIMINGS.openDelay,
      skipDelayDuration: options.skipDelayDuration ?? PANEL_TIMINGS.skipDelay,
      leaveDelay: options.leaveDelay ?? PANEL_TIMINGS.leaveDelay,
      enabled: !isMobile,
    });

    const isPanelVisible = isMobile ? isOpen : isHovered;

    const triggerProps = isMobile
      ? { onClick: togglePanel }
      : {};

    const handlePanelActivate = (): void => {
      const container = containerRef.current;
      if (!container) return;

      container.removeAttribute('data-panel-disabled');

      if (!isMobile) {
        updatePanelPosition(container);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Escape') {
        containerRef.current?.setAttribute('data-panel-disabled', 'true');

        const focusable = containerRef.current?.querySelector('a, button');
        if (focusable instanceof HTMLElement) {
          focusable.focus();
        }
      }
    };

    return {
      isPanelVisible,
      triggerProps,
      containerProps: {
        onMouseEnter: handlePanelActivate,
        onFocus: handlePanelActivate,
        onKeyDown: handleKeyDown,
      },
      panelRef,
    };
  };
